import ChipsAutocomplete from "components/shared/ChipsAutocomplete";
import type { IBeneficiar } from "containers/Beneficiars/types";
import useAPIPagination from "hooks/useAPIPagination";
import React from "react";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "store/store";

type Option = { id: number; name: string };

interface IBeneficiaryPickerFilter {
    value: number[];
    onChange: (ids: number[]) => void;
}

const buildLabel = (b: IBeneficiar): string => {
    const surname = (b.surname ?? "").toUpperCase();
    const name = b.name ?? "";
    return `${surname} ${name}`.trim() || `#${b.id}`;
};

export const BeneficiaryPickerFilter = ({ value, onChange }: IBeneficiaryPickerFilter) => {
    const [t] = useTranslation();

    const { handleQuery, requestStatus, query } = useAPIPagination({
        type: "beneficiaries",
        defaultLimitPagination: 10,
        autocomplete: true,
    });

    const beneficiariesAutocomplete = useAppSelector((state) => state.beneficiary.beneficiarsAutocomplete);

    const options: Option[] = useMemo(
        () => beneficiariesAutocomplete.map((b) => ({ id: b.id, name: buildLabel(b) })),
        [beneficiariesAutocomplete],
    );

    const nameById = useRef(new Map<number, string>());
    const chosenTags = useMemo(
        () => value.map((id) => ({ id, name: nameById.current.get(id) ?? `#${id}`, color: 0 })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [value, nameById.current.size],
    );

    // Remember every (id → name) pair we've seen so deselect still works after
    // the autocomplete buffer is replaced by a new search.
    useEffect(() => {
        options.forEach((o) => nameById.current.set(o.id, o.name));
        chosenTags.forEach((t) => {
            if (!options.find((o) => o.id === t.id)) {
                options.push({ id: t.id, name: t.name });
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options]);

    const setChosenTags = (names: string[]) => {
        const idByName = new Map<string, number>();
        nameById.current.forEach((name, id) => idByName.set(name, id));
        const ids = names.map((name) => idByName.get(name)).filter((id): id is number => id !== undefined);
        onChange(ids);
    };

    return (
        <ChipsAutocomplete
            size="medium"
            limitTags={2}
            options={options}
            label={t("Filters.Beneficiaries")}
            noOptionsText={t("Filters.NoResultFound")}
            filter={query}
            chosenTags={chosenTags}
            setChosenTags={setChosenTags as React.Dispatch<React.SetStateAction<string[]>>}
            isLoading={requestStatus === "pending"}
            onInputChange={(value, reason) => {
                if (reason !== "input") return;
                handleQuery({ target: { value } } as React.ChangeEvent<HTMLInputElement>);
            }}
        />
    );
};
